import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import QRCode from 'qrcode.react'
import { Link2, Sparkles, X, Copy } from 'lucide-react'
import { showSuccessToast } from '../../Utlis/Toast'
import { useAppDispatch, useAppSelector } from '../../../../Redux/Hooks'
import { getEventFormTokenAPI } from '../../../../Redux/ApiCalls/Dashboard/EventAPI'
import LoadingDots from '../../Utlis/LoadinDots'
import { removeCurrentFormToken } from '../../../../Redux/Slice/Dashboard/ExtraSlice'
import ShareButton from '../ShareCode/ShareButtonModal'
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
  gradient,
} from '../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles'

interface ShareFormLinkProps {
  onClose: () => void
}

const ShareEventFormLinkPopup: React.FC<ShareFormLinkProps> = ({ onClose }) => {
  const [url, setUrl] = useState('')
  const dispatch = useAppDispatch()
  const { loading, eventFromToken } = useAppSelector((state) => state.extra)

  useEffect(() => {
    dispatch(getEventFormTokenAPI())
    return () => {
      dispatch(removeCurrentFormToken())
    }
  }, [dispatch])

  useEffect(() => {
    if (eventFromToken) {
      setUrl(`https://${window.location.host}/share/form/${eventFromToken}`)
    }
  }, [eventFromToken])

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => {
      showSuccessToast('Url copied to clipboard.')
    })
  }

  return (
    <ModalOverlay onClick={onClose}>
      <Shell onClick={(e) => e.stopPropagation()}>
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroText>
              <HeroBadge>
                <Sparkles size={10} />
                Event inquiries
              </HeroBadge>
              <ModalTitle>Share Event Form</ModalTitle>
              <ModalSubtitle>
                Scan the QR code or copy the link to share your event form.
              </ModalSubtitle>
            </HeroText>
            <CloseBtn type="button" onClick={onClose}>
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <Body>
          {loading ? (
            <LoadingContainer>
              <LoadingText>Generating form link…</LoadingText>
              <LoadingDots />
            </LoadingContainer>
          ) : (
            <>
              <QrCard>
                <QrFrame>
                  <QRCode value={url || ' '} size={177} />
                </QrFrame>
                <ShareButton shareUrl={url} />
              </QrCard>

              <UrlCard>
                <UrlLabel>
                  <Link2 size={14} />
                  Form URL
                </UrlLabel>
                <UrlRow>
                  <UrlInput type="text" value={url} readOnly />
                  <CopyBtn type="button" onClick={handleCopy} aria-label="Copy URL">
                    <Copy size={16} />
                    Copy
                  </CopyBtn>
                </UrlRow>
              </UrlCard>
            </>
          )}
        </Body>
      </Shell>
    </ModalOverlay>
  )
}

export default ShareEventFormLinkPopup

const Shell = styled(ModalShell)`
  max-width: 480px;
  overflow: visible;
`

const Body = styled(ModalBody)`
  overflow: visible;
  padding-bottom: 28px;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`

const LoadingText = styled.p`
  margin: 0;
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.55);
`

const QrCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  padding: 24px 24px 36px;
  margin-bottom: 28px;
  position: relative;
  background: #fff;
  border-radius: 18px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04);
`

const QrFrame = styled.div`
  width: 220px;
  height: 220px;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(201, 77, 170, 0.12);
  border: 1px solid rgba(201, 77, 170, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
`

const UrlCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.04);
`

const UrlLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`

const UrlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const UrlInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 42px;
  padding: 0 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.1);
  border-radius: 12px;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
  &:focus {
    outline: none;
    border-color: rgba(201, 77, 170, 0.4);
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.1);
  }
`

const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 42px;
  padding: 0 16px;
  border: none;
  border-radius: 12px;
  background: ${gradient};
  color: #fff;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 6px 16px rgba(201, 77, 170, 0.22);
  transition: transform 0.15s ease;
  &:hover { transform: translateY(-1px); }
`
